import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ACTIVE_TAB_PARAM } from '../constants';
import { useAppDispatch } from '../hooks';
import { addCall, updateCall } from '../redux/reducers';
import { socket } from '../services';
import { Call, CallKind, ReviewTab, Roles } from '../types';

enum CallEvent {
  // Real Call lifecycle events
  REAL_CALL_CREATION = 'real-call:creation:started',
  REAL_CALL_CREATION_COMPLETED = 'real-call:creation:completed',
  REAL_CALL_CREATION_FAILED = 'real-call:creation:failed',

  // Call lifecycle events
  CALL_STARTED = 'call:started',
  CALL_ENDED = 'call:ended',

  // Processing events
  CALL_PROCESSING = 'call:processing',
  CALL_PROCESSED = 'call:processed',
  CALL_PROCESSING_FAILED = 'call:processing:failed',

  // Scorecard events
  CALL_SCORING_STARTED = 'call:scoring:started',
  CALL_SCORING_COMPLETED = 'call:scoring:completed',
  CALL_SCORING_FAILED = 'call:scoring:failed',

  // Scored questions events
  CALL_SCORED_QUESTIONS_SCORING_STARTED = 'call:scored-questions:scoring:started',
  CALL_SCORED_QUESTIONS_SCORING_COMPLETED = 'call:scored-questions:scoring:completed',
  CALL_SCORED_QUESTIONS_SCORING_FAILED = 'call:scored-questions:scoring:failed',

  // Coaching events
  CALL_COACHING_STARTED = 'call:coaching:started',
  CALL_COACHING_COMPLETED = 'call:coaching:completed',
  CALL_COACHING_FAILED = 'call:coaching:failed',
}

type CallEventResponse = {
  data: {
    call: Call;
    callerRole: Roles;
  };
};

const useCallSocketEvents = () => {
  const dispatch = useAppDispatch();

  const [searchParams] = useSearchParams();

  const handleAddCall = useCallback(
    (response: CallEventResponse) => {
      const activeTab = searchParams.get(ACTIVE_TAB_PARAM);

      const isSimulationTab = activeTab === ReviewTab.SIMULATIONS;
      const eventCallKind = response.data.call.callKind;

      // Only add call if current event matches current tab
      if (
        (isSimulationTab && eventCallKind === CallKind.SIMULATION) ||
        (!isSimulationTab && eventCallKind === CallKind.REAL)
      ) {
        dispatch(addCall(response.data.call));
      }
    },
    [dispatch, searchParams]
  );

  const handleUpdateCall = useCallback(
    (response: CallEventResponse) => {
      dispatch(updateCall(response.data.call));
    },
    [dispatch]
  );

  useEffect(() => {
    socket.on(CallEvent.REAL_CALL_CREATION, handleAddCall);
    socket.on(CallEvent.REAL_CALL_CREATION_COMPLETED, handleUpdateCall);
    socket.on(CallEvent.REAL_CALL_CREATION_FAILED, handleUpdateCall);

    socket.on(CallEvent.CALL_STARTED, handleAddCall);
    socket.on(CallEvent.CALL_ENDED, handleUpdateCall);

    socket.on(CallEvent.CALL_PROCESSING, handleUpdateCall);
    socket.on(CallEvent.CALL_PROCESSED, handleUpdateCall);
    socket.on(CallEvent.CALL_PROCESSING_FAILED, handleUpdateCall);

    socket.on(CallEvent.CALL_COACHING_STARTED, handleUpdateCall);
    socket.on(CallEvent.CALL_COACHING_COMPLETED, handleUpdateCall);
    socket.on(CallEvent.CALL_COACHING_FAILED, handleUpdateCall);

    socket.on(CallEvent.CALL_SCORING_STARTED, handleUpdateCall);
    socket.on(CallEvent.CALL_SCORING_COMPLETED, handleUpdateCall);
    socket.on(CallEvent.CALL_SCORING_FAILED, handleUpdateCall);

    socket.on(CallEvent.CALL_SCORED_QUESTIONS_SCORING_STARTED, handleUpdateCall);
    socket.on(CallEvent.CALL_SCORED_QUESTIONS_SCORING_COMPLETED, handleUpdateCall);
    socket.on(CallEvent.CALL_SCORED_QUESTIONS_SCORING_FAILED, handleUpdateCall);

    // Clean up the event listeners when the component unmounts
    return () => {
      socket.off(CallEvent.REAL_CALL_CREATION, handleAddCall);
      socket.off(CallEvent.REAL_CALL_CREATION_COMPLETED, handleUpdateCall);
      socket.off(CallEvent.REAL_CALL_CREATION_FAILED, handleUpdateCall);

      socket.off(CallEvent.CALL_STARTED, handleAddCall);
      socket.off(CallEvent.CALL_ENDED, handleUpdateCall);

      socket.off(CallEvent.CALL_PROCESSING, handleUpdateCall);
      socket.off(CallEvent.CALL_PROCESSED, handleUpdateCall);
      socket.off(CallEvent.CALL_PROCESSING_FAILED, handleUpdateCall);

      socket.off(CallEvent.CALL_COACHING_STARTED, handleUpdateCall);
      socket.off(CallEvent.CALL_COACHING_COMPLETED, handleUpdateCall);
      socket.off(CallEvent.CALL_COACHING_FAILED, handleUpdateCall);

      socket.off(CallEvent.CALL_SCORING_STARTED, handleUpdateCall);
      socket.off(CallEvent.CALL_SCORING_COMPLETED, handleUpdateCall);
      socket.off(CallEvent.CALL_SCORING_FAILED, handleUpdateCall);

      socket.off(CallEvent.CALL_SCORED_QUESTIONS_SCORING_STARTED, handleUpdateCall);
      socket.off(CallEvent.CALL_SCORED_QUESTIONS_SCORING_COMPLETED, handleUpdateCall);
      socket.off(CallEvent.CALL_SCORED_QUESTIONS_SCORING_FAILED, handleUpdateCall);
    };
  }, [socket, dispatch, handleAddCall, handleUpdateCall]);
};

export default useCallSocketEvents;
